import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	HostListener,
	OnDestroy,
	OnInit,
	Output,
	ViewEncapsulation,
	AfterViewInit,
	EventEmitter,
} from "@angular/core";
import { Store } from "@ngrx/store";
import { LoginUiFlowService } from "@obg/common.login/public-api";
import { LoginFlowDialogService } from "@obg/mat.ui.common";
import {
	AUTH_MESSAGE_CODES,
	AUTH_MESSAGE_CONTEXT,
	CORE_ROUTE,
	CoreState,
	LoadingService,
	MessageService,
	MessageType,
	SessionConfigService,
} from "@obg/ngx.core";
import { TranslationGroup, TranslationSelector } from "@obg/ngx.core.content";
import { PlatformService, WindowRef } from "@obg/ngx.global.evo";
import { RouteAction } from "@obg/ngx.router";
import { Observable, Subscription } from "rxjs";
import { filter, map, tap } from "rxjs/operators";

import { LoginFormService } from "../../login-form.service";
import { SITE_LOGIN_TRANSLATIONS } from "../../login.model";
@Component({
	selector: "obg-m-login-with-service",
	templateUrl: "./login-with-service.container.html",
	styleUrls: ["./login-with-service.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class LoginWithServiceContainer implements OnDestroy, OnInit, AfterViewInit {
	@HostBinding("class.obg-m-login-with-service") hostCssClass = true;
	@Output() busy = new EventEmitter<boolean>();

	translations$: Observable<Record<string, string | undefined>> | undefined;
	isLoginMfeEnable: boolean | undefined;
	checked: boolean | undefined;
	error: string | undefined;
	customerCanLogInAt: string | undefined;
	invalidCredentials$: Subscription | undefined;
	sessionLimitExpired$: Subscription | undefined;
	disallowLogin = false;

	constructor(
		private store: Store<CoreState>,
		private loadingService: LoadingService,
		private loginFlowDialogService: LoginFlowDialogService,
		private platformService: PlatformService,
		private window: WindowRef,
		private loginFormService: LoginFormService,
		private translationSelector: TranslationSelector,
		private messageService: MessageService,
		private changeDetector: ChangeDetectorRef,
		private loginUiFlowService: LoginUiFlowService,
		private sessionConfigService: SessionConfigService
	) {}

	ngOnInit() {
		this.setVhVariable();
		this.translations$ = this.store.pipe(
			this.translationSelector.getByKeys_v2$(SITE_LOGIN_TRANSLATIONS.translations, [TranslationGroup.Common]),
			map((keys) => Object.fromEntries(keys.map((_, i) => [SITE_LOGIN_TRANSLATIONS.translations[i], keys[i]])))
		);
		this.fabricErrors();
		this.disallowLogin = this.sessionConfigService.isCountryBlockingEnabled();
	}

	ngAfterViewInit() {
		this.isLoginMfeEnable = this.loginFormService.mfeLoginFlag();
	}

	ngOnDestroy() {
		this.loadingService.close();
		this.loginFlowDialogService.closeAllDialogs();
		this.invalidCredentials$?.unsubscribe();
		this.sessionLimitExpired$?.unsubscribe();
	}

	@HostListener("window:resize")
	onResize() {
		this.setVhVariable();
	}

	setVhVariable() {
		if (this.platformService.isBrowser) {
			const vh = this.window.native.innerHeight;
			this.window.native.document.documentElement.style.setProperty("--virtual-height", `${vh}px`);
		}
	}

	handleLoading(isBusy: boolean) {
		if (isBusy) {
			this.loadingService.open();
			return;
		}
		this.loadingService.close();
	}

	navigateBack() {
		this.store.dispatch(RouteAction.back());
	}

	rememberMeToggle(checked: boolean) {
		this.checked = checked;
	}

	routeLinks(link: string) {
		if (link === "registration") {
			this.store.dispatch(RouteAction.navigate(CORE_ROUTE.registration.register));
		} else if (link === "forgetPassword") {
			this.store.dispatch(RouteAction.navigate(CORE_ROUTE.auth.forgotPassword));
		}
	}

	loginValues(form: any) {
		if (form.username === "" || form.password === "") {
			this.error = `login.${AUTH_MESSAGE_CODES.login.invalidCredentials.toLowerCase()}`;
			return;
		}
		this.loginFormService.loginFabric(form);
	}

	// The handle of errors will be part of the Login Migration SDK in a service
	// Those 2 errors has been done in the component as a temporary solution
	fabricErrors() {
		this.invalidCredentials$ = this.messageService
			.consume$({
				context: AUTH_MESSAGE_CONTEXT.login,
				messageType: MessageType.Error,
				messageCode: AUTH_MESSAGE_CODES.login.invalidCredentials,
			})
			.pipe(
				filter((message) => !!message.length),
				tap((message) => {
					this.busy.emit(false);
					this.error = `login.${message[0].messageCode.toLowerCase()}`;
				})
			)
			.subscribe(() => this.changeDetector.markForCheck());

		this.sessionLimitExpired$ = this.loginUiFlowService
			.sessionLimitExpired$()
			.pipe(
				tap((error) => {
					this.busy.emit(false);
					this.customerCanLogInAt = error.customerCanLogInAt;
					this.error = `login.${error?.code}`.toLowerCase();
				})
			)
			.subscribe(() => this.changeDetector.markForCheck());
	}
}
